import React, { useEffect, useMemo, useRef, useState } from "react";
import { Send, Save, RotateCcw, Sparkles, FlaskConical, Loader2, BookOpen } from "lucide-react";
import { adminApi, getActiveAgent } from "@/lib/api";
import { toast } from "sonner";
import { renderMarkdown } from "@/lib/format";

const MODELS = [
    { id: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5", provider: "Anthropic" },
    { id: "gpt-4o", label: "GPT-4o", provider: "OpenAI" },
    { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash", provider: "Google" },
];

const ACCENT = { priya: "#7E2A6C", tara: "#0F766E" };

const newSessionId = () =>
    "pg-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const greetingFor = (name) =>
    `Namaskar! I'm ${name}. Type a question on the right to test how I respond with these instructions.`;

export default function Playground() {
    const activeAgentId = getActiveAgent();
    const defaultName = activeAgentId === "tara" ? "Tara" : "Priya";
    const accent = ACCENT[activeAgentId] || ACCENT.priya;
    const [agentName, setAgentName] = useState(defaultName);

    // Editable agent config
    const [instructions, setInstructions] = useState("");
    const [savedInstructions, setSavedInstructions] = useState("");
    const [model, setModel] = useState(MODELS[0].id);
    const [savedModel, setSavedModel] = useState(MODELS[0].id);
    const [temperature, setTemperature] = useState(0.3);
    const [savedTemperature, setSavedTemperature] = useState(0.3);
    const [loadingCfg, setLoadingCfg] = useState(true);
    const [savingCfg, setSavingCfg] = useState(false);

    // Live chat state (Playground only — not persisted to public chat)
    const [messages, setMessages] = useState([
        { role: "assistant", text: greetingFor(defaultName) },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(newSessionId);
    const [showChunks, setShowChunks] = useState(false);
    const [lastChunks, setLastChunks] = useState([]);
    const scrollRef = useRef(null);

    // Load saved config on mount
    useEffect(() => {
        (async () => {
            try {
                const { data } = await adminApi.get("/api/admin/settings/ai");
                const nm = data.name || defaultName;
                setAgentName(nm);
                setMessages([{ role: "assistant", text: greetingFor(nm) }]);
                setInstructions(data.base_instructions || "");
                setSavedInstructions(data.base_instructions || "");
                setModel(data.llm_model || MODELS[0].id);
                setSavedModel(data.llm_model || MODELS[0].id);
                setTemperature(data.temperature ?? 0.3);
                setSavedTemperature(data.temperature ?? 0.3);
            } catch (e) {
                toast.error("Could not load AI settings");
            } finally {
                setLoadingCfg(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    const isDirty = useMemo(
        () =>
            instructions !== savedInstructions ||
            model !== savedModel ||
            Math.abs(temperature - savedTemperature) > 0.0001,
        [instructions, savedInstructions, model, savedModel, temperature, savedTemperature]
    );

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || sending) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text }]);
        setSending(true);
        try {
            const { data } = await adminApi.post("/api/admin/playground", {
                question: text,
                instructions,
                model,
                temperature,
                session_id: sessionId,
            });
            setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
            setLastChunks(data.context_chunks || []);
        } catch (err) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    text: "Error: " + (err.response?.data?.detail || err.message),
                    error: true,
                },
            ]);
        } finally {
            setSending(false);
        }
    };

    const resetChat = async () => {
        setMessages([{ role: "assistant", text: greetingFor(agentName) }]);
        setLastChunks([]);
        const oldSid = sessionId;
        const newSid = newSessionId();
        setSessionId(newSid);
        // Best-effort clear of server-side history for the old session
        try {
            await adminApi.post(`/api/admin/playground/reset?session_id=${encodeURIComponent(oldSid)}`);
        } catch (e) {
            // non-blocking
        }
    };

    const saveConfig = async () => {
        setSavingCfg(true);
        try {
            await adminApi.put("/api/admin/settings/ai", {
                base_instructions: instructions,
                llm_model: model,
                temperature,
            });
            setSavedInstructions(instructions);
            setSavedModel(model);
            setSavedTemperature(temperature);
            toast.success(`Saved — ${agentName} is now using these settings on the live widget.`);
        } catch (err) {
            toast.error("Save failed: " + (err.response?.data?.detail || err.message));
        } finally {
            setSavingCfg(false);
        }
    };

    const revert = () => {
        setInstructions(savedInstructions);
        setModel(savedModel);
        setTemperature(savedTemperature);
        toast.info("Reverted to saved settings");
    };

    if (loadingCfg) {
        return (
            <div className="p-10 text-stone-500 flex items-center gap-2" data-testid="playground-loading">
                <Loader2 className="animate-spin" size={16} /> Loading playground…
            </div>
        );
    }

    const tempLabel = temperature <= 0.3 ? "Reserved" : temperature <= 0.7 ? "Balanced" : "Creative";

    return (
        <div className="h-screen flex flex-col" data-testid="page-playground">
            {/* Page header */}
            <div className="px-8 pt-7 pb-4 border-b border-stone-200 bg-white flex items-end justify-between flex-shrink-0">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">
                        Test &amp; Tune
                    </div>
                    <h1 className="font-heading text-4xl text-orchid-700">Playground</h1>
                    <p className="text-stone-600 text-sm mt-1.5">
                        Edit {agentName}&apos;s instructions on the left, chat with {agentName} on the
                        right. Save to apply changes to the live widget.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isDirty && (
                        <span
                            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-gold-500/15 text-gold-700"
                            data-testid="unsaved-badge"
                        >
                            Unsaved changes
                        </span>
                    )}
                    <button
                        onClick={revert}
                        disabled={!isDirty}
                        data-testid="playground-revert"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50 disabled:opacity-40"
                    >
                        Revert
                    </button>
                    <button
                        onClick={saveConfig}
                        disabled={!isDirty || savingCfg}
                        data-testid="playground-save"
                        className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-xs font-medium px-4 py-2 rounded-md disabled:opacity-50"
                    >
                        {savingCfg ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        {savingCfg ? "Saving…" : "Save & deploy"}
                    </button>
                </div>
            </div>

            {/* Split body */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(420px,2fr)_3fr] overflow-hidden">
                {/* LEFT — instructions / model / temperature */}
                <aside className="border-r border-stone-200 bg-stone-50 flex flex-col min-h-0">
                    <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-2 text-stone-700">
                        <FlaskConical size={15} className="text-orchid-700" />
                        <span className="text-sm font-medium">Agent configuration</span>
                    </div>
                    <div className="flex-1 overflow-y-auto scroll-soft px-6 py-5 space-y-6">
                        {/* Model */}
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-2">
                                Model
                            </div>
                            <div className="grid grid-cols-1 gap-1.5">
                                {MODELS.map((m) => (
                                    <button
                                        key={m.id}
                                        onClick={() => setModel(m.id)}
                                        data-testid={`pg-model-${m.id}`}
                                        className={`text-left px-3 py-2 rounded-md border transition-colors ${
                                            model === m.id
                                                ? "border-orchid-700 bg-orchid-700/5"
                                                : "border-stone-200 bg-white hover:border-stone-300"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                                                    {m.provider}
                                                </div>
                                                <div className="text-sm font-medium text-stone-900">
                                                    {m.label}
                                                </div>
                                            </div>
                                            {model === m.id && (
                                                <Sparkles size={14} className="text-orchid-700" />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Temperature */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                    Temperature
                                </div>
                                <div className="text-xs text-stone-700 font-medium">
                                    {tempLabel} · {temperature.toFixed(1)}
                                </div>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={temperature}
                                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                                data-testid="pg-temp"
                                className="w-full accent-orchid-700"
                            />
                            <div className="flex justify-between text-[9px] uppercase tracking-wider text-stone-400 mt-1">
                                <span>Reserved</span>
                                <span>Balanced</span>
                                <span>Creative</span>
                            </div>
                        </div>

                        {/* Instructions */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                    Instructions
                                </div>
                                <div className="text-[10px] text-stone-400">
                                    {instructions.length.toLocaleString()} chars
                                </div>
                            </div>
                            <textarea
                                value={instructions}
                                onChange={(e) => setInstructions(e.target.value)}
                                data-testid="pg-instructions"
                                rows={20}
                                spellCheck={false}
                                className="w-full px-3 py-2.5 rounded-md border border-stone-200 bg-white text-[12.5px] font-mono leading-relaxed text-stone-800 focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                            />
                            <p className="text-[11px] text-stone-500 mt-1.5">
                                Drives every reply on the live widget. Tested instantly on the right
                                — save to apply.
                            </p>
                        </div>
                    </div>
                </aside>

                {/* RIGHT — live chat */}
                <section className="flex flex-col min-h-0 bg-paper">
                    <div className="px-6 py-3.5 border-b border-stone-200 bg-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div
                                className="h-8 w-8 rounded-full text-white flex items-center justify-center text-xs font-medium"
                                style={{ background: accent }}
                            >
                                {(agentName || "P").charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <div className="text-sm font-medium text-stone-900">{agentName}</div>
                                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                                    Playground · live test
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowChunks((s) => !s)}
                                data-testid="pg-toggle-chunks"
                                className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border transition-colors ${
                                    showChunks
                                        ? "border-orchid-700 bg-orchid-700/5 text-orchid-700"
                                        : "border-stone-200 text-stone-600 hover:bg-stone-50"
                                }`}
                            >
                                <BookOpen size={12} />
                                {lastChunks.length} chunks
                            </button>
                            <button
                                onClick={resetChat}
                                data-testid="pg-reset"
                                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50"
                            >
                                <RotateCcw size={12} />
                                Reset
                            </button>
                        </div>
                    </div>

                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto scroll-soft px-6 py-5 space-y-3"
                        data-testid="pg-chat-scroll"
                    >
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                data-testid={`pg-msg-${i}`}
                            >
                                <div
                                    className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                        m.role === "user"
                                            ? "bg-orchid-700 text-white rounded-br-sm"
                                            : m.error
                                            ? "bg-red-50 border border-red-200 text-red-900 rounded-bl-sm"
                                            : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"
                                    }`}
                                >
                                    <div
                                        className="bot-text"
                                        dangerouslySetInnerHTML={{
                                            __html: renderMarkdown(m.text),
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start" data-testid="pg-typing">
                                <div className="bg-white border border-stone-200 px-3.5 py-2.5 rounded-2xl rounded-bl-sm">
                                    <div className="flex gap-1">
                                        <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-pulse" />
                                        <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-pulse [animation-delay:.15s]" />
                                        <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-pulse [animation-delay:.3s]" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {showChunks && lastChunks.length > 0 && (
                            <div
                                className="bg-stone-50 border border-stone-200 rounded-lg p-3 mt-4"
                                data-testid="pg-chunks"
                            >
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-sage-700 mb-2">
                                    <BookOpen size={11} /> Retrieved knowledge ({lastChunks.length})
                                </div>
                                <div className="space-y-1.5">
                                    {lastChunks.map((c, i) => (
                                        <div
                                            key={i}
                                            className="bg-white border border-stone-200 rounded p-2 text-[11px] text-stone-700 whitespace-pre-wrap font-mono leading-relaxed"
                                        >
                                            {c.slice(0, 400)}
                                            {c.length > 400 ? "…" : ""}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={send}
                        className="px-6 py-4 border-t border-stone-200 bg-white flex gap-2"
                        data-testid="pg-form"
                    >
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={`Ask ${agentName} anything to test…`}
                            data-testid="playground-chat-input"
                            className="flex-1 px-3 py-2.5 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                        />
                        <button
                            type="submit"
                            disabled={sending || !input.trim()}
                            data-testid="playground-send-button"
                            className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2.5 rounded-md disabled:opacity-50"
                        >
                            {sending ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Send size={14} />
                            )}
                            Send
                        </button>
                    </form>
                </section>
            </div>
        </div>
    );
}
